import type { EmailThread } from '@core/types';

export type EmailDeliveryChipKind = 'queued' | 'sent' | 'opened' | 'bounced';

/** Derive delivery state from thread row. `bounced` only when optional DB field is present. */
export function emailDeliveryKind(thread: EmailThread): EmailDeliveryChipKind {
  const bounceAt = (thread as { bounce_detected_at?: string | null }).bounce_detected_at;
  if (bounceAt) return 'bounced';
  if (!thread.sent_at) return 'queued';
  if (thread.opened_at || thread.open_count > 0) return 'opened';
  return 'sent';
}

const CHIP_BASE =
  'text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold border border-[color-mix(in_srgb,var(--vscode-border-primary)_65%,transparent)]';

export function emailDeliveryChipClass(kind: EmailDeliveryChipKind): string {
  switch (kind) {
    case 'queued':
      return `${CHIP_BASE} bg-[color-mix(in_srgb,var(--vscode-text-link)_10%,var(--vscode-bg-secondary))] vscode-text-secondary`;
    case 'sent':
      return `${CHIP_BASE} bg-[var(--vscode-bg-secondary)] vscode-text-primary`;
    case 'opened':
      return `${CHIP_BASE} bg-[color-mix(in_srgb,var(--vscode-text-link)_14%,var(--vscode-bg-secondary))] vscode-text-primary`;
    case 'bounced':
    default:
      return `${CHIP_BASE} bg-[color-mix(in_srgb,#ef4444_12%,var(--vscode-bg-secondary))] text-red-600 dark:text-red-300`;
  }
}

export function emailDeliveryLabel(kind: EmailDeliveryChipKind): string {
  switch (kind) {
    case 'queued':
      return 'Queued';
    case 'sent':
      return 'Sent';
    case 'opened':
      return 'Opened';
    case 'bounced':
      return 'Bounced';
    default:
      return 'Sent';
  }
}

/** Dot colour for StatusChip (§2.13 — neutral pill + dot). */
export function emailDeliveryDotColor(kind: EmailDeliveryChipKind): string {
  switch (kind) {
    case 'bounced':
      return '#ef4444';
    case 'opened':
    case 'sent':
      return '#3b82f6';
    case 'queued':
    default:
      return 'var(--vscode-text-tertiary)';
  }
}
